'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';
import { BrandLogo, SiteHeader } from '@/app/components/layout/SiteHeader';

type QuickCard = {
  title: string;
  description: string;
  href: string;
  icon: string;
};

type SaleCard = {
  type: string;
  trend: string;
  trendUp: boolean;
  price: string;
  address: string;
  postcode: string;
  meta: string[];
  soldDate: string;
  gradient: string;
};

type TrendRow = {
  label: string;
  value: string;
  width: string;
};

type PostcodeRow = {
  rank: number;
  code: string;
  area: string;
  price: string;
  trend: string;
  trendUp: boolean;
};

type PostcodeInputType = 'AREA' | 'DISTRICT' | 'SECTOR' | 'UNIT' | 'UNKNOWN';

type PostcodeInputMatch = {
  type: PostcodeInputType;
  normalized: string;
  compact: string;
};

const hintChips = ['SW1W 0RB', 'M1', 'E1', 'BS1', 'LS1'];

const quickCards: QuickCard[] = [
  {
    title: 'Sold prices by postcode',
    description: 'Transaction history and market stats by postcode, district, or sector.',
    href: '#postcode-search',
    icon: '🏡',
  },
  {
    title: 'Market trends',
    description: 'Track movement, velocity, and price direction across local areas.',
    href: '/analytics',
    icon: '📈',
  },
  {
    title: 'Interactive map',
    description: 'Browse areas visually and move straight into postcode-level discovery.',
    href: '/map',
    icon: '🗺️',
  },
  {
    title: 'Area reports',
    description: 'Snapshot context for transport, schools, and neighbourhood signals.',
    href: '/analytics',
    icon: '📊',
  },
];

const recentSales: SaleCard[] = [
  {
    type: 'Terraced',
    trend: '+11.3%',
    trendUp: true,
    price: '£875,000',
    address: '45 Kensington Gardens',
    postcode: 'SW7 4PY',
    meta: ['4 beds', '2 baths', '2,100 sqft'],
    soldDate: 'Sold 2 Apr 2026',
    gradient: 'from-amber-200 via-orange-100 to-emerald-100',
  },
  {
    type: 'Semi-Detached',
    trend: '+7.2%',
    trendUp: true,
    price: '£625,000',
    address: 'The Willows, Hampstead',
    postcode: 'NW3 2TQ',
    meta: ['3 beds', '2 baths', '1,460 sqft'],
    soldDate: 'Sold 28 Mar 2026',
    gradient: 'from-green-200 via-emerald-100 to-slate-100',
  },
  {
    type: 'Apartment',
    trend: '-2.1%',
    trendUp: false,
    price: '£1,250,000',
    address: 'Chelsea Riverside Apartments',
    postcode: 'SW10 0JD',
    meta: ['2 beds', '2 baths', '1,020 sqft'],
    soldDate: 'Sold 1 Apr 2026',
    gradient: 'from-sky-200 via-blue-100 to-slate-100',
  },
  {
    type: 'Detached',
    trend: '+5.8%',
    trendUp: true,
    price: '£1,850,000',
    address: 'Oakwood Drive, Richmond',
    postcode: 'TW10 5AQ',
    meta: ['5 beds', '3 baths', '3,200 sqft'],
    soldDate: 'Sold 5 Apr 2026',
    gradient: 'from-lime-200 via-green-100 to-emerald-100',
  },
  {
    type: 'Maisonette',
    trend: '+3.4%',
    trendUp: true,
    price: '£450,000',
    address: '14 Barnsbury Square',
    postcode: 'N1 1JY',
    meta: ['2 beds', '1 bath', '820 sqft'],
    soldDate: 'Sold 31 Mar 2026',
    gradient: 'from-stone-200 via-zinc-100 to-emerald-100',
  },
  {
    type: 'Bungalow',
    trend: '-0.9%',
    trendUp: false,
    price: '£385,000',
    address: '22 Elm Park Road',
    postcode: 'HA5 3LW',
    meta: ['3 beds', '1 bath', '1,050 sqft'],
    soldDate: 'Sold 3 Apr 2026',
    gradient: 'from-amber-100 via-stone-100 to-emerald-100',
  },
];

const marketRows: TrendRow[] = [
  { label: 'Detached', value: '£479,200', width: '100%' },
  { label: 'Semi-det.', value: '£298,400', width: '62%' },
  { label: 'Terraced', value: '£244,100', width: '51%' },
  { label: 'Flat', value: '£198,700', width: '41%' },
  { label: 'London', value: '£521,600', width: '88%' },
];

const topPostcodes: PostcodeRow[] = [
  { rank: 1, code: 'W1K', area: 'Mayfair', price: '£2.84M', trend: '+4.2%', trendUp: true },
  { rank: 2, code: 'SW3', area: 'Chelsea', price: '£2.11M', trend: '-1.8%', trendUp: false },
  { rank: 3, code: 'SW7', area: 'Kensington', price: '£1.97M', trend: '+1.1%', trendUp: true },
  { rank: 4, code: 'NW8', area: "St John's Wood", price: '£1.74M', trend: '+2.6%', trendUp: true },
  { rank: 5, code: 'EC2', area: 'City of London', price: '£1.51M', trend: '+3.4%', trendUp: true },
];

const areaRegex = /^[A-Z]{1,2}$/;
const districtRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?$/;
const sectorRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?[0-9]$/;
const unitRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?[0-9][A-Z]{2}$/;

const unknownSuggestion =
  'Digite uma área (SW), distrito (SW17), sector (SW17 8) ou postcode completo (SW17 8HS)';

const postcodeTypeLabel: Record<Exclude<PostcodeInputType, 'UNKNOWN'>, string> = {
  AREA: 'Postcode Area',
  DISTRICT: 'Postcode District',
  SECTOR: 'Postcode Sector',
  UNIT: 'Full Postcode',
};

function normalizePostcodeInput(input: string): { normalized: string; compact: string } {
  const normalized = input
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return { normalized, compact: normalized.replace(/\s+/g, '') };
}

function identifyPostcodeInput(input: string): PostcodeInputMatch {
  const { normalized, compact } = normalizePostcodeInput(input);
  if (!compact) return { type: 'UNKNOWN', normalized, compact };
  if (areaRegex.test(compact)) return { type: 'AREA', normalized, compact };
  if (districtRegex.test(compact)) return { type: 'DISTRICT', normalized, compact };
  if (sectorRegex.test(compact)) return { type: 'SECTOR', normalized, compact };
  if (unitRegex.test(compact)) return { type: 'UNIT', normalized, compact };
  return { type: 'UNKNOWN', normalized, compact };
}

function buildMapHref(postcode: string): string {
  const normalized = postcode.trim();
  return normalized ? `/map?postcode=${encodeURIComponent(normalized)}` : '/map';
}

function buildPostcodePath(postcode: string): string {
  const normalized = normalizePostcodeInput(postcode).compact.toLowerCase();
  return normalized ? `/${encodeURIComponent(normalized)}` : '/';
}

export default function HomeRedesign() {
  const router = useRouter();
  const [postcode, setPostcode] = useState('SW1W 0RB');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mapHref = useMemo(() => buildMapHref(postcode), [postcode]);
  const postcodeMatch = useMemo(() => identifyPostcodeInput(postcode), [postcode]);
  const shouldShowUnknownSuggestion = postcodeMatch.compact.length > 0 && postcodeMatch.type === 'UNKNOWN';
  const postcodeTypeTag =
    postcodeMatch.type === 'UNKNOWN' ? null : postcodeTypeLabel[postcodeMatch.type];

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!postcodeMatch.compact) {
      setError('Please enter a UK postcode, district, or sector.');
      return;
    }

    setLoading(true);
    router.push(buildPostcodePath(postcodeMatch.normalized));
  }

  return (
    <main className="min-h-screen bg-[#faf8f4] text-[#0f0f0d]">
      <SiteHeader mapReportHref={mapHref} />

      <section className="relative overflow-hidden px-4 pb-14 pt-14 text-center sm:px-6 sm:pt-20">
        <div className="pointer-events-none absolute left-1/2 top-[-180px] h-[460px] w-[900px] -translate-x-1/2 bg-[radial-gradient(ellipse_at_center,rgba(26,74,58,0.1)_0%,transparent_70%)]" />
        <div className="relative mx-auto max-w-4xl">
          <p className="mx-auto inline-flex items-center gap-2 rounded-full bg-[#d4ede5] px-3 py-1 text-xs font-medium uppercase tracking-wide text-[#2d7a5f]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#3bb37a]" />
            Updated monthly · England & Wales
          </p>
          <h1 className="mx-auto mt-6 max-w-4xl text-balance font-serif text-4xl font-bold leading-tight tracking-tight sm:text-6xl">
            Property prices.
            <br />
            Your <span className="text-[#2d7a5f]">postcode</span>.
            <br />
            The full picture.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base font-light leading-7 text-[#7a7870] sm:text-lg">
            Enter any UK postcode or town to see sold homes nearby, what they went for,
            <br /> and how prices are moving in that area.
          </p>

          <form id="postcode-search" onSubmit={handleSubmit} className="mx-auto mt-8 max-w-2xl">
            <div className="flex items-center rounded-2xl border border-black/20 bg-white p-1.5 pl-4 shadow-md">
              <input
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="Enter a postcode (e.g. SE1 7PB) or town"
                className="min-w-0 flex-1 bg-transparent text-sm text-[#0f0f0d] placeholder:text-[#b8b5ae] focus:outline-none"
                autoComplete="postal-code"
                required
              />
              <button
                type="submit"
                disabled={loading}
                className="rounded-xl bg-[#1a4a3a] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#2d7a5f] disabled:opacity-60"
              >
                {loading ? 'Searching...' : 'Search →'}
              </button>
            </div>
          </form>

          {postcodeTypeTag && (
            <p className="mx-auto mt-3 max-w-2xl text-left text-xs font-medium uppercase tracking-wide text-[#2d7a5f]">
              {postcodeTypeTag}
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            {hintChips.map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => setPostcode(chip)}
                className="rounded-full border border-black/10 bg-[#f3f0ea] px-3 py-1 text-xs text-[#7a7870] transition hover:border-[#d4ede5] hover:bg-[#d4ede5] hover:text-[#1a4a3a]"
              >
                {chip}
              </button>
            ))}
          </div>

          {shouldShowUnknownSuggestion && (
            <div className="mx-auto mt-4 max-w-2xl rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-800">
              {unknownSuggestion}
            </div>
          )}

          {error && (
            <div className="mx-auto mt-4 max-w-2xl rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-left text-sm text-rose-700">
              {error}
            </div>
          )}
        </div>
      </section>

      <section className="border-y border-black/10 bg-white">
        <div className="mx-auto grid max-w-6xl grid-cols-2 sm:grid-cols-4">
          <StatBlock value="2.5M+" label="Properties tracked" />
          <StatBlock value="1.7M+" label="Postcodes covered" />
          <StatBlock value="10K+" label="Monthly updates" />
          <StatBlock value="1995" label="Data going back to" />
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-2 pt-14 sm:px-6">
        <div className="mb-5 flex items-center justify-between gap-3">
          <h2 className="font-serif text-3xl font-semibold">Explore the data</h2>
          <Link href="/analytics" className="text-sm font-medium text-[#2d7a5f] hover:underline">
            Browse all tools →
          </Link>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {quickCards.map((card) => (
            <Link
              key={card.title}
              href={card.href}
              className="rounded-xl border border-black/10 bg-white p-5 transition hover:-translate-y-0.5 hover:border-[#2d7a5f] hover:shadow-sm"
            >
              <div className="mb-3 inline-flex rounded-lg bg-[#f3f0ea] p-2 text-lg">{card.icon}</div>
              <h3 className="text-sm font-semibold">{card.title}</h3>
              <p className="mt-2 text-sm text-[#7a7870]">{card.description}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-2 pt-14 sm:px-6">
        <div className="grid overflow-hidden rounded-3xl bg-[#0f0f0d] lg:grid-cols-[1fr_1.1fr]">
          <div className="p-8 sm:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#85d4b4]">
              Interactive map
            </p>
            <h2 className="mt-4 font-serif text-4xl font-semibold leading-tight text-white">
              See where homes sold — on the map
            </h2>
            <p className="mt-4 max-w-md text-sm leading-6 text-white/65">
              Navigate our interactive price map to explore sold properties across England & Wales.
              Zoom into any street and instantly review local prices.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                href={mapHref}
                className="rounded-lg bg-[#3bb37a] px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
              >
                Explore the map →
              </Link>
              <a
                href="#recent-sales"
                className="rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white/80"
              >
                How it works
              </a>
            </div>
          </div>
          <div className="relative min-h-[280px] bg-[linear-gradient(180deg,#1a2e28,#213932)] p-8">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(59,179,122,0.22),transparent_45%),radial-gradient(circle_at_80%_70%,rgba(255,255,255,0.16),transparent_35%)]" />
            <MapPin className="left-[10%] top-[16%]" label="£485,000" tone="green" />
            <MapPin className="left-[45%] top-[28%]" label="£1.2M" tone="light" />
            <MapPin className="left-[25%] top-[54%]" label="£320,000" tone="amber" />
            <MapPin className="left-[68%] top-[60%]" label="£760,000" tone="green" />
            <MapPin className="left-[72%] top-[26%]" label="£890,000" tone="glass" />
          </div>
        </div>
      </section>

      <section id="recent-sales" className="mx-auto max-w-6xl px-4 pb-2 pt-14 sm:px-6">
        <div className="mb-5 flex items-center justify-between gap-3">
          <h2 className="font-serif text-3xl font-semibold">Recent sales in London</h2>
          <a href="#postcode-search" className="text-sm font-medium text-[#2d7a5f] hover:underline">
            View all →
          </a>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {recentSales.map((sale) => (
            <article key={`${sale.address}-${sale.type}`} className="overflow-hidden rounded-3xl border border-black/10 bg-white">
              <div className={`relative h-44 bg-gradient-to-br ${sale.gradient}`}>
                <span className="absolute left-3 top-3 rounded-full bg-black/60 px-2 py-1 text-xs font-medium text-white">
                  {sale.type}
                </span>
                <span
                  className={`absolute right-3 top-3 rounded-full px-2 py-1 text-xs font-medium ${sale.trendUp ? 'bg-[#d4ede5] text-[#1a4a3a]' : 'bg-rose-100 text-rose-700'}`}
                >
                  {sale.trend}
                </span>
              </div>
              <div className="p-5">
                <p className="font-serif text-3xl font-semibold">{sale.price}</p>
                <p className="mt-1 text-sm text-[#3a3935]">{sale.address}</p>
                <p className="mt-0.5 font-mono text-xs text-[#b8b5ae]">{sale.postcode}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {sale.meta.map((item) => (
                    <span key={item} className="rounded-full bg-[#f3f0ea] px-2.5 py-1 text-xs text-[#7a7870]">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-black/10 bg-[#faf8f4] px-5 py-3">
                <span className="text-xs text-[#b8b5ae]">{sale.soldDate}</span>
                <a href="#postcode-search" className="text-xs font-medium text-[#2d7a5f]">
                  View details →
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-2 pt-14 sm:px-6">
        <div className="grid gap-8 rounded-3xl bg-[#1a4a3a] p-8 sm:p-10 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <h2 className="font-serif text-4xl font-semibold text-white">Beyond the sold price</h2>
            <p className="mt-4 text-sm leading-6 text-white/70">
              Every postcode search comes with context: schools, crime, transport, flood risk, EPC,
              and market signals in one place.
            </p>
            <a
              href="#postcode-search"
              className="mt-6 inline-flex rounded-lg bg-white px-4 py-2 text-sm font-medium text-[#1a4a3a]"
            >
              See what is included
            </a>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <FeatureItem icon="🏫" title="Schools & Ofsted" text="Nearest schools with ratings and local context." />
            <FeatureItem icon="🚨" title="Crime statistics" text="12-month trend breakdown by category." />
            <FeatureItem icon="🚂" title="Transport links" text="Commute context and station proximity." />
            <FeatureItem icon="🌊" title="Flood risk" text="Flood zone context and planning awareness." />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-2 pt-14 sm:px-6">
        <div className="mb-5 flex items-center justify-between gap-3">
          <h2 className="font-serif text-3xl font-semibold">Market snapshot</h2>
          <Link href="/analytics" className="text-sm font-medium text-[#2d7a5f] hover:underline">
            Full market trends →
          </Link>
        </div>
        <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
          <article className="overflow-hidden rounded-3xl border border-black/10 bg-white">
            <header className="flex items-center justify-between border-b border-black/10 px-5 py-4">
              <p className="text-sm font-medium">Average sold price by property type · England & Wales</p>
              <span className="rounded-full bg-[#d4ede5] px-2 py-0.5 text-xs font-medium text-[#1a4a3a]">
                Mar 2026
              </span>
            </header>
            <div className="space-y-4 p-5">
              {marketRows.map((row) => (
                <div key={row.label} className="grid grid-cols-[80px_1fr_70px] items-center gap-3">
                  <span className="text-xs text-[#7a7870]">{row.label}</span>
                  <div className="h-2 overflow-hidden rounded-full bg-[#f3f0ea]">
                    <div style={{ width: row.width }} className="h-full rounded-full bg-[#2d7a5f]" />
                  </div>
                  <span className="text-right text-xs font-medium text-[#3a3935]">{row.value}</span>
                </div>
              ))}
            </div>
          </article>

          <article className="overflow-hidden rounded-3xl border border-black/10 bg-white">
            <header className="flex items-center justify-between border-b border-black/10 px-5 py-4">
              <p className="text-sm font-medium">Top postcodes by avg. price</p>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                London
              </span>
            </header>
            <div>
              {topPostcodes.map((item) => (
                <div key={item.code} className="grid grid-cols-[18px_52px_1fr_auto_auto] items-center gap-3 border-b border-black/10 px-5 py-3 last:border-b-0">
                  <span className="text-xs text-[#b8b5ae]">{item.rank}</span>
                  <span className="font-mono text-sm font-medium">{item.code}</span>
                  <span className="text-sm text-[#7a7870]">{item.area}</span>
                  <span className="text-sm font-medium">{item.price}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${item.trendUp ? 'bg-[#d4ede5] text-[#1a4a3a]' : 'bg-rose-100 text-rose-700'}`}
                  >
                    {item.trend}
                  </span>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pt-14 sm:px-6">
        <div className="flex flex-wrap items-center gap-4 rounded-3xl border border-black/10 bg-[#f3f0ea] p-6 sm:p-8">
          <div className="min-w-56 flex-1">
            <h3 className="font-serif text-2xl font-semibold">Built on official UK government data</h3>
            <p className="mt-2 text-sm text-[#7a7870]">
              Price data sourced from HM Land Registry and updated monthly.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full border border-black/15 bg-white px-3 py-1">HM Land Registry</span>
            <span className="rounded-full border border-black/15 bg-white px-3 py-1">data.police.uk</span>
            <span className="rounded-full border border-black/15 bg-white px-3 py-1">Environment Agency</span>
            <span className="rounded-full border border-black/15 bg-white px-3 py-1">Ofsted / DfE</span>
            <span className="rounded-full border border-black/15 bg-white px-3 py-1">EPC Register</span>
          </div>
        </div>
      </section>

      <footer className="mt-16 border-t border-black/10 bg-white px-4 py-10 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <Link href="/" className="flex items-center gap-2">
                <BrandLogo />
              </Link>
              <p className="mt-3 max-w-sm text-sm text-[#7a7870]">
                Accurate UK property data for buyers, sellers, investors, and professionals.
              </p>
            </div>
            <FooterCol
              title="Product"
              links={[
                { label: 'Search', href: '#postcode-search' },
                { label: 'Market Trends', href: '/analytics' },
                { label: 'Map', href: '/map' },
              ]}
            />
            <FooterCol
              title="Resources"
              links={[
                { label: 'Data Sources', href: '#postcode-search' },
                { label: 'Reports', href: '/analytics' },
                { label: 'Help', href: '/map' },
              ]}
            />
            <FooterCol
              title="Legal"
              links={[
                { label: 'Privacy', href: '/robots.txt' },
                { label: 'Terms', href: '/sitemap.xml' },
              ]}
            />
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-2 border-t border-black/10 pt-5 text-xs text-[#b8b5ae]">
            <p>© 2026 PostcodePrices. Contains HM Land Registry data © Crown copyright.</p>
            <div className="flex items-center gap-4">
              <a href="#postcode-search" className="hover:text-[#7a7870]">
                Cookies
              </a>
              <a href="#postcode-search" className="hover:text-[#7a7870]">
                Accessibility
              </a>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}

function StatBlock({ value, label }: { value: string; label: string }) {
  return (
    <div className="border-b border-black/10 px-3 py-5 text-center last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
      <div className="font-serif text-3xl font-semibold">{value}</div>
      <div className="mt-1 text-[11px] uppercase tracking-wide text-[#7a7870]">{label}</div>
    </div>
  );
}

function FeatureItem({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div className="rounded-xl border border-white/15 bg-white/10 p-4">
      <span className="text-xl">{icon}</span>
      <h3 className="mt-2 text-sm font-medium text-white">{title}</h3>
      <p className="mt-1 text-xs leading-5 text-white/65">{text}</p>
    </div>
  );
}

function MapPin({
  label,
  tone,
  className,
}: {
  label: string;
  tone: 'green' | 'light' | 'amber' | 'glass';
  className: string;
}) {
  const toneClass =
    tone === 'green'
      ? 'bg-[#3bb37a] text-white'
      : tone === 'light'
        ? 'bg-white text-[#0f0f0d]'
        : tone === 'amber'
          ? 'bg-amber-500 text-white'
          : 'border border-white/20 bg-white/15 text-white backdrop-blur';

  return (
    <div className={`absolute ${className}`}>
      <div className={`rounded-full px-2.5 py-1 text-xs font-semibold shadow ${toneClass}`}>{label}</div>
      <div className={`mx-auto h-2 w-0.5 ${tone === 'amber' ? 'bg-amber-500' : 'bg-white/70'}`} />
      <div
        className={`mx-auto h-1.5 w-1.5 rounded-full ${tone === 'green' ? 'bg-[#3bb37a]' : tone === 'amber' ? 'bg-amber-500' : 'bg-white/70'}`}
      />
    </div>
  );
}

function FooterCol({
  title,
  links,
}: {
  title: string;
  links: { label: string; href: string }[];
}) {
  return (
    <div>
      <h4 className="mb-3 text-xs font-semibold uppercase tracking-[0.14em]">{title}</h4>
      <ul className="space-y-2">
        {links.map((link) => (
          <li key={link.label}>
            <Link href={link.href} className="text-sm text-[#7a7870] transition hover:text-[#0f0f0d]">
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
